import React, { useState } from 'react';

const SneakerOutfitMatcher = () => {
  const [sneakerImage, setSneakerImage] = useState(null);
  const [dominantColors, setDominantColors] = useState([]);
  const [outfitSuggestions, setOutfitSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOutfit, setSelectedOutfit] = useState({
    top: null,
    bottom: null,
    outerwear: null
  });

  const extractColors = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        const colorMap = {};

        for (let i = 0; i < pixels.length; i += 4) {
          const r = Math.round(pixels[i] / 51) * 51;
          const g = Math.round(pixels[i + 1] / 51) * 51;
          const b = Math.round(pixels[i + 2] / 51) * 51;
          const key = `${r},${g},${b}`;
          colorMap[key] = (colorMap[key] || 0) + 1;
        }

        const sortedColors = Object.entries(colorMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([color]) => {
            const [r, g, b] = color.split(',');
            return `rgb(${r},${g},${b})`;
          });

        resolve(sortedColors);
      };
      img.src = imageUrl;
    });
  };

  const generateOutfitSuggestions = (colors) => {
    // Helper function to determine if a color is light or dark
    const isLightColor = (rgbString) => {
      const rgb = rgbString.match(/\d+/g).map(Number);
      const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
      return brightness > 155;
    };

    // Helper function to get color name from RGB
    const getColorName = (rgbString) => {
      const rgb = rgbString.match(/\d+/g).map(Number);
      const [r, g, b] = rgb;
      
      // Determine color family
      if (r > 200 && g > 200 && b > 200) return { name: 'White', hex: '#FFFFFF' };
      if (r < 50 && g < 50 && b < 50) return { name: 'Black', hex: '#000000' };
      if (r > 150 && g < 100 && b < 100) return { name: 'Red', hex: `rgb(${r},${g},${b})` };
      if (r > 200 && g > 150 && b < 150) return { name: 'Pink', hex: `rgb(${r},${g},${b})` };
      if (r < 100 && g < 100 && b > 150) return { name: 'Blue', hex: `rgb(${r},${g},${b})` };
      if (r < 150 && g > 150 && b < 100) return { name: 'Green', hex: `rgb(${r},${g},${b})` };
      if (r > 150 && g > 100 && b < 100) return { name: 'Orange', hex: `rgb(${r},${g},${b})` };
      if (r > 150 && g > 150 && b < 100) return { name: 'Yellow', hex: `rgb(${r},${g},${b})` };
      if (r > 100 && g < 100 && b > 100) return { name: 'Purple', hex: `rgb(${r},${g},${b})` };
      if (r > 100 && g > 80 && b < 80) return { name: 'Brown', hex: `rgb(${r},${g},${b})` };
      if (Math.abs(r - g) < 30 && Math.abs(g - b) < 30) {
        if (r > 150) return { name: 'Light Gray', hex: `rgb(${r},${g},${b})` };
        if (r > 80) return { name: 'Gray', hex: `rgb(${r},${g},${b})` };
        return { name: 'Dark Gray', hex: `rgb(${r},${g},${b})` };
      }
      return { name: 'Neutral', hex: `rgb(${r},${g},${b})` };
    };

    // Get primary colors from sneaker
    const primaryColor = getColorName(colors[0]);
    const accentColor = colors[1] ? getColorName(colors[1]) : primaryColor;
    const hasLightColors = colors.some(c => isLightColor(c));
    const hasDarkColors = colors.some(c => !isLightColor(c));

    // Build dynamic outfit suggestions
    const topSuggestions = [];
    const bottomSuggestions = [];
    const outerwearSuggestions = [];

    // TOPS - always include neutrals + accent colors
    topSuggestions.push({
      name: 'White T-Shirt',
      reason: 'Clean neutral that makes your sneakers the focus',
      color: 'White',
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop'
    });

    topSuggestions.push({
      name: 'Black Hoodie',
      reason: 'Classic streetwear staple that works with any sneaker',
      color: 'Black',
      image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=400&fit=crop'
    });

    // Add color-coordinated top based on sneaker colors
    if (primaryColor.name.includes('Blue')) {
      topSuggestions.push({
        name: 'Navy Blue Tee',
        reason: `Matches the ${primaryColor.name.toLowerCase()} tones in your sneakers`,
        color: 'Navy Blue',
        image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=400&fit=crop'
      });
    } else if (primaryColor.name.includes('Red') || primaryColor.name.includes('Pink')) {
      topSuggestions.push({
        name: 'Light Pink Tee',
        reason: `Coordinates with the ${primaryColor.name.toLowerCase()} accents`,
        color: 'Light Pink',
        image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=400&fit=crop'
      });
    } else if (primaryColor.name.includes('Green') || primaryColor.name.includes('Brown')) {
      topSuggestions.push({
        name: 'Olive Tee',
        reason: `Complements the earth tones in your kicks`,
        color: 'Olive/Tan',
        image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=400&fit=crop'
      });
    } else {
      topSuggestions.push({
        name: 'Gray Oversized Tee',
        reason: 'Neutral tone that balances your sneaker colors',
        color: 'Gray',
        image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=400&fit=crop'
      });
    }

    // BOTTOMS - vary based on shoe brightness
    if (hasLightColors) {
      bottomSuggestions.push({
        name: 'Light Wash Denim',
        reason: 'Light tones complement your sneakers without competing',
        color: 'Light Blue',
        image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=400&fit=crop'
      });
      bottomSuggestions.push({
        name: 'Khaki Chinos',
        reason: 'Neutral earth tone that lets light sneakers shine',
        color: 'Tan/Khaki',
        image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&h=400&fit=crop&sat=-100'
      });
    } else {
      bottomSuggestions.push({
        name: 'Black Joggers',
        reason: 'Dark bottoms create contrast with your sneakers',
        color: 'Black',
        image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&h=400&fit=crop'
      });
      bottomSuggestions.push({
        name: 'Dark Denim',
        reason: 'Classic look that grounds bold sneakers',
        color: 'Dark Blue',
        image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=400&fit=crop'
      });
    }

    // Always add cargo pants as versatile option
    bottomSuggestions.push({
      name: 'Cargo Pants',
      reason: 'Trendy streetwear look that works with any kicks',
      color: 'Olive/Black',
      image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&h=400&fit=crop&sat=-100'
    });

    // OUTERWEAR - based on color coordination
    if (primaryColor.name.includes('Blue') || accentColor.name.includes('Blue')) {
      outerwearSuggestions.push({
        name: 'Denim Jacket',
        reason: `Blue denim coordinates perfectly with your ${primaryColor.name.toLowerCase()} sneakers`,
        color: 'Blue Denim',
        image: 'https://images.unsplash.com/photo-1576995853123-5a10305d93c0?w=400&h=400&fit=crop'
      });
    } else {
      outerwearSuggestions.push({
        name: 'Black Bomber',
        reason: 'Versatile layer that works with any sneaker color',
        color: 'Black',
        image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=400&fit=crop'
      });
    }

    outerwearSuggestions.push({
      name: 'Track Jacket',
      reason: 'Sporty vibe matches the athletic aesthetic of your kicks',
      color: 'Black/Navy',
      image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=400&fit=crop'
    });

    // Add colored jacket if there's a strong accent color
    if (accentColor.name !== 'Black' && accentColor.name !== 'White') {
      outerwearSuggestions.push({
        name: 'Windbreaker',
        reason: `Accent color picks up the ${accentColor.name.toLowerCase()} in your sneakers`,
        color: accentColor.name,
        image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=400&fit=crop'
      });
    } else {
      outerwearSuggestions.push({
        name: 'Gray Hoodie',
        reason: 'Neutral layer that lets your sneakers stand out',
        color: 'Gray',
        image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=400&fit=crop'
      });
    }

    return [
      { category: 'Top', items: topSuggestions },
      { category: 'Bottom', items: bottomSuggestions },
      { category: 'Outerwear', items: outerwearSuggestions }
    ];
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageUrl = event.target.result;
        setSneakerImage(imageUrl);
        
        const colors = await extractColors(imageUrl);
        setDominantColors(colors);
        
        const suggestions = generateOutfitSuggestions(colors);
        setOutfitSuggestions(suggestions);
        setLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const reset = () => {
    setSneakerImage(null);
    setDominantColors([]);
    setOutfitSuggestions([]);
    setSelectedOutfit({ top: null, bottom: null, outerwear: null });
  };

  const selectItem = (category, item) => {
    setSelectedOutfit(prev => ({
      ...prev,
      [category.toLowerCase()]: item
    }));
  };

  const hasCompleteOutfit = selectedOutfit.top && selectedOutfit.bottom;

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(to bottom right, #0f172a, #881337, #0f172a)', padding: '1rem'}}>
      <div style={{maxWidth: '64rem', margin: '0 auto'}}>
        <div style={{textAlign: 'center', padding: '2rem 0'}}>
          <h1 style={{fontSize: '4rem', fontWeight: '900', color: 'white'}}>
            Kicks<span style={{background: 'linear-gradient(to right, #ef4444, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>2</span>Fit
          </h1>
          <p style={{color: '#9ca3af', fontSize: '1.125rem'}}>
            Upload Your Kicks. Build the Fit.
          </p>
        </div>

        {!sneakerImage ? (
          <div style={{background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '1rem', padding: '2rem', border: '1px solid rgba(255,255,255,0.2)'}}>
            <label style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '3rem', border: '2px dashed rgba(239,68,68,0.5)', borderRadius: '0.75rem'}}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{display: 'none'}}
              />
              <div style={{width: '5rem', height: '5rem', marginBottom: '1rem', borderRadius: '50%', background: 'linear-gradient(to bottom right, #ef4444, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2rem'}}>
                ↑
              </div>
              <p style={{color: 'white', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem'}}>Upload Your Sneakers</p>
              <p style={{color: '#d1d5db', fontSize: '0.875rem'}}>Click to browse or drag and drop</p>
            </label>
          </div>
        ) : (
          <div>
            <div style={{background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '1rem', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '1.5rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'white'}}>Your Sneakers</h2>
                <button
                  onClick={reset}
                  style={{color: 'white', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.5rem'}}
                >
                  ✕
                </button>
              </div>
              <img
                src={sneakerImage}
                alt="Uploaded sneaker"
                style={{width: '100%', height: '16rem', objectFit: 'contain', background: 'rgba(255,255,255,0.05)', borderRadius: '0.75rem'}}
              />
              
              {dominantColors.length > 0 && (
                <div style={{marginTop: '1rem'}}>
                  <p style={{color: 'white', fontWeight: '600', marginBottom: '0.5rem'}}>
                    <span style={{display: 'inline-block', width: '0.5rem', height: '0.5rem', background: 'linear-gradient(to right, #ef4444, #ec4899)', borderRadius: '50%', marginRight: '0.5rem'}}></span>
                    Dominant Colors:
                  </p>
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    {dominantColors.map((color, idx) => (
                      <div
                        key={idx}
                        style={{width: '3rem', height: '3rem', borderRadius: '0.5rem', border: '2px solid rgba(255,255,255,0.3)', backgroundColor: color}}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div style={{textAlign: 'center', padding: '3rem 0'}}>
                <div style={{display: 'inline-block', width: '3rem', height: '3rem', border: '4px solid rgba(239,68,68,0.3)', borderTop: '4px solid #ef4444', borderRadius: '50%'}}></div>
                <p style={{color: 'white', marginTop: '1rem'}}>Generating outfit suggestions...</p>
              </div>
            ) : (
              <div>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem'}}>Outfit Suggestions</h2>
                
                {hasCompleteOutfit && (
                  <div style={{background: 'linear-gradient(to bottom right, rgba(239,68,68,0.2), rgba(236,72,153,0.2))', backdropFilter: 'blur(10px)', borderRadius: '1rem', padding: '1.5rem', border: '2px solid rgba(239,68,68,0.5)', marginBottom: '1.5rem'}}>
                    <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem'}}>
                      ✨ Your Complete Fit
                    </h3>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem'}}>
                      {selectedOutfit.top && (
                        <div style={{textAlign: 'center'}}>
                          <div style={{height: '10rem', background: '#1f2937', borderRadius: '0.75rem', overflow: 'hidden', marginBottom: '0.5rem'}}>
                            <img 
                              src={selectedOutfit.top.image} 
                              alt={selectedOutfit.top.name}
                              style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            />
                          </div>
                          <p style={{color: 'white', fontWeight: '600', fontSize: '0.875rem'}}>{selectedOutfit.top.name}</p>
                        </div>
                      )}
                      {selectedOutfit.bottom && (
                        <div style={{textAlign: 'center'}}>
                          <div style={{height: '10rem', background: '#1f2937', borderRadius: '0.75rem', overflow: 'hidden', marginBottom: '0.5rem'}}>
                            <img 
                              src={selectedOutfit.bottom.image} 
                              alt={selectedOutfit.bottom.name}
                              style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            />
                          </div>
                          <p style={{color: 'white', fontWeight: '600', fontSize: '0.875rem'}}>{selectedOutfit.bottom.name}</p>
                        </div>
                      )}
                      {selectedOutfit.outerwear && (
                        <div style={{textAlign: 'center'}}>
                          <div style={{height: '10rem', background: '#1f2937', borderRadius: '0.75rem', overflow: 'hidden', marginBottom: '0.5rem'}}>
                            <img 
                              src={selectedOutfit.outerwear.image} 
                              alt={selectedOutfit.outerwear.name}
                              style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            />
                          </div>
                          <p style={{color: 'white', fontWeight: '600', fontSize: '0.875rem'}}>{selectedOutfit.outerwear.name}</p>
                        </div>
                      )}
                      <div style={{textAlign: 'center'}}>
                        <div style={{height: '10rem', background: '#1f2937', borderRadius: '0.75rem', overflow: 'hidden', marginBottom: '0.5rem', padding: '0.5rem'}}>
                          <img 
                            src={sneakerImage} 
                            alt="Your sneakers"
                            style={{width: '100%', height: '100%', objectFit: 'contain'}}
                          />
                        </div>
                        <p style={{color: 'white', fontWeight: '600', fontSize: '0.875rem'}}>Your Kicks</p>
                      </div>
                    </div>
                    <p style={{textAlign: 'center', color: '#d1d5db', fontSize: '0.875rem', marginTop: '1rem'}}>
                      Click items below to customize your fit
                    </p>
                  </div>
                )}
                
                {outfitSuggestions.map((category, idx) => (
                  <div
                    key={idx}
                    style={{background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '1rem', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '1rem'}}
                  >
                    <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem'}}>
                      {category.category}
                    </h3>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem'}}>
                      {category.items.map((item, itemIdx) => {
                        const isSelected = selectedOutfit[category.category.toLowerCase()] === item;
                        return (
                          <div
                            key={itemIdx}
                            onClick={() => selectItem(category.category, item)}
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              borderRadius: '0.75rem',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: isSelected ? '2px solid #ef4444' : 'none'
                            }}
                          >
                            <div style={{position: 'relative', height: '12rem', background: '#1f2937', overflow: 'hidden'}}>
                              <img 
                                src={item.image} 
                                alt={item.name}
                                style={{width: '100%', height: '100%', objectFit: 'cover'}}
                              />
                              {isSelected && (
                                <div style={{position: 'absolute', top: '0.5rem', right: '0.5rem', background: '#ef4444', color: 'white', borderRadius: '50%', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>
                                  ✓
                                </div>
                              )}
                            </div>
                            <div style={{padding: '1rem'}}>
                              <h4 style={{color: 'white', fontWeight: '600', marginBottom: '0.25rem'}}>{item.name}</h4>
                              <p style={{color: '#f87171', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem'}}>Color: {item.color}</p>
                              <p style={{color: '#d1d5db', fontSize: '0.875rem'}}>{item.reason}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{textAlign: 'center', padding: '2rem 0', color: '#9ca3af', fontSize: '0.875rem'}}>
          <p>Kicks2Fit™ MVP v1.0 - Built for sneakerheads, by sneakerheads</p>
        </div>
      </div>
    </div>
  );
};

export default SneakerOutfitMatcher;

